import { LoadingOverlay, Stack, Tooltip } from "@mantine/core"
import { useForm, zodResolver } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { showNotification } from "@mantine/notifications"
import { IconInfoCircle } from "@tabler/icons-react"
import { ErrorBox, DateInput, EqualWidthGrid } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import {
  CertificateUpdateDto,
  certificateUpdateSchema,
} from "@tfg/types/schemas/modules/traceability/commands/update-certificate/update-certificate.request.dto"

import classes from "~/assets/css/Certificates.module.css"
import { queryClient } from "~/backend/queryClient"
import {
  findCertificatesByOrganizationIdQueryKey,
  FindCertificatesByOrganizationIdResponseDto,
} from "~/gen/api"
import { RenderField } from "~/pages/Admin/components/RenderField"
import { FormFieldConfig } from "~/pages/Admin/components/TypesForm"
import { CertificateFormSubmitButton } from "~/pages/Brand/Certificates/components/CertificateFormSubmitButton"
import { CertificateProductionSite } from "~/pages/Brand/Certificates/components/CertificateProductionSite"
import { CertificateReference } from "~/pages/Brand/Certificates/components/CertificateReference"
import { CertificateTypeSelector } from "~/pages/Brand/Certificates/components/CertificateTypeSelector"
import { RejectionModal } from "~/pages/Brand/Certificates/components/RejectionModal"
import { SuccessModal } from "~/pages/Brand/Certificates/components/SuccessModal"
import {
  filterCertificatesWithTemplate,
  FilteredCertificate,
  getCertificateFormFieldType,
  getCertificateTemplate,
  getTypeOfKey,
} from "~/pages/Brand/Certificates/components/templates.ts"
import { useUpdateCertificateMutation } from "~/pages/Brand/Certificates/hooks/useUpdateCertificateMutation.ts"
import {
  labelHolderTranslations,
  placeHolderTranslations,
} from "~/pages/Brand/Certificates/type.ts"

type Props = {
  certificate: FindCertificatesByOrganizationIdResponseDto
  onClose: () => void
}

export function CertificateForm({ certificate, onClose }: Props) {
  const { t } = useTranslation("certificates")

  const form = useForm({
    validate: zodResolver(certificateUpdateSchema),
    initialValues: {
      typeId: certificate.type.id,
      reference: certificate.reference ?? undefined,
      issueDate: certificate.issueDate ? new Date(certificate.issueDate) : null,
      expiryDate: certificate.expiryDate
        ? new Date(certificate.expiryDate)
        : null,
      productionSiteId: certificate.productionSite?.id ?? null,
      score: certificate.score,
      status: certificate.status,
      rejectionMessage: certificate.rejectionMessage,
      name: certificate.name,
      productCategories: certificate.productCategories || [],
      processingSteps: certificate.processingSteps || [],
      productDetails: certificate.productDetails || [],
      buyerOfCertifiedProducts: certificate.buyerOfCertifiedProducts || null,
      certifiedCompanyAddress: certificate.certifiedCompanyAddress || null,
      certifiedWeight: certificate.certifiedWeight || null,
      linkedScopeCertificateNumber:
        certificate.linkedScopeCertificateNumber || null,
      certificationBody: certificate.certificationBody || null,
      certifiedCompanyLicenseNumber:
        certificate.certifiedCompanyLicenseNumber || null,
      certifiedCompanyName: certificate.certifiedCompanyName || null,
    } as CertificateUpdateDto,
  })
  const {
    mutateAsync: updateCertificate,
    isLoading,
    error,
  } = useUpdateCertificateMutation(certificate.id, {
    onSuccess: async (_data, variables) => {
      onClose()
      const message =
        variables.status === "REJECTED"
          ? t("modal.successReject")
          : t("modal.success")
      form.reset()
      showNotification({
        color: "green",
        message,
      })
      await queryClient.invalidateQueries(
        findCertificatesByOrganizationIdQueryKey()
      )
    },
    onError: (error) => {
      showNotification({
        color: "red",
        message: error.message,
      })
    },
  })
  const [rejectModalOpened, rejectModalHandler] = useDisclosure(false)
  const [successModalOpened, successModalHandlers] = useDisclosure(false)

  const { filteredCertificates, fields } = useMemo(() => {
    const template = getCertificateTemplate(certificate.type.category)
    const filteredCerts = filterCertificatesWithTemplate(
      certificate,
      template,
      true
    )
    if (!filteredCerts) {
      return { filteredCertificates: null, fields: [], template }
    }
    const keys = Object.keys(filteredCerts).filter(
      (key) => key !== "expiryDate" && key !== "reference"
    ) as Array<keyof FilteredCertificate>
    const formFields: FormFieldConfig[] = keys.map((key) => {
      const type = getTypeOfKey(key, certificate)
      return {
        type: getCertificateFormFieldType(type),
        name: key,
        label: t(labelHolderTranslations[key as keyof FilteredCertificate]),
        placeholder: t(
          placeHolderTranslations[key as keyof FilteredCertificate]
        ),
        customClass: classes,
      }
    })
    return { filteredCertificates: filteredCerts, fields: formFields, template }
  }, [certificate, t])

  if (!filteredCertificates) {
    return null
  }

  const successModalHandleClose = async () => {
    successModalHandlers.close()
    form.setFieldValue("status", "PROCESSED")
    await updateCertificate(form.getValues())
  }

  const rejectModalHandleSubmit = async () => {
    const { rejectionMessage, status } = form.getValues()
    await updateCertificate({ status, rejectionMessage })
    rejectModalHandler.close()
  }
  return (
    <form onSubmit={form.onSubmit((values) => updateCertificate(values))}>
      <LoadingOverlay visible={isLoading} />
      <ErrorBox error={error} />
      <Stack
        gap="var(--tfg-padding-molecule-padding-y)"
        h="80vh"
        className={classes.form}
        w="100%"
      >
        <Stack className={classes.formBody}>
          {certificate.productionSite && (
            <CertificateProductionSite certificate={certificate} />
          )}
          <CertificateTypeSelector form={form} />
          <EqualWidthGrid spacing="xs">
            <DateInput
              size="xs"
              label={t("modal.labels.issueDate")}
              placeholder={t("modal.placeholders.issueDate")}
              classNames={classes}
              {...form.getInputProps("issueDate")}
            />
            <DateInput
              size="xs"
              label={t("modal.labels.expiryDate")}
              placeholder={t("modal.placeholders.expiryDate")}
              bg="white"
              color="white"
              classNames={classes}
              disabled={!("expiryDate" in filteredCertificates)}
              {...form.getInputProps("expiryDate")}
              value={
                "expiryDate" in filteredCertificates
                  ? form.getInputProps("expiryDate").value
                  : null
              }
              rightSection={
                !("expiryDate" in filteredCertificates) ? (
                  <Tooltip label={t("tips.expiryDateNotRequired")}>
                    <IconInfoCircle size={12} />
                  </Tooltip>
                ) : null
              }
            />
          </EqualWidthGrid>
          <CertificateReference
            reference={form.getValues().reference ?? null}
            onChange={form.getInputProps("reference").onChange}
            typeName={certificate.type.name}
            certificateName={certificate.name}
          />
          {/*OPTIONAL FIELDS HERE*/}
          {fields.map((fields) => RenderField({ fields, form }))}
        </Stack>
        <Stack h="10%" pos="sticky" w="100%">
          <CertificateFormSubmitButton
            form={form}
            rejectHandler={rejectModalHandler}
            successModalHandler={successModalHandlers}
          />
        </Stack>
      </Stack>
      <RejectionModal
        form={form}
        opened={rejectModalOpened}
        onClose={rejectModalHandler.close}
        onSubmit={rejectModalHandleSubmit}
      />
      {successModalOpened && (
        <SuccessModal
          certificate={certificate}
          form={form}
          opened={successModalOpened}
          onClose={successModalHandlers.close}
          onSubmit={successModalHandleClose}
        />
      )}
    </form>
  )
}
